/**
 * Internationalization module.
 */

import countryList from 'vendor/magnum-ui/utils/country/list';
import {
	createCookie,
	readCookie,
	COOKIE_USER_COUNTRY_CODE,
	COOKIE_USER_COUNTRY_NAME,
	COOKIE_USER_LOCATION_COUNTRY_CODE,
	COOKIE_USER_CURRENCY_CODE,
} from 'scripts/modules/_cookies';
import { toTitleCase } from 'scripts/helpers/_strings';
import countryToCurrency from 'scripts/json/country-to-currency.json';

export const i18nVars = window.fbVars && window.fbVars.i18n ? window.fbVars.i18n : {};

/**
 * Gets site's country as 2-character code.
 *
 * @param {string} [format=upper] Set casing of return value.
 * @return {string} 2 character country code w/ chosen casing.
 */
export const getSiteCountryCode = ( format = 'upper' ) => {
	const countryCode = i18nVars.siteCountryCode || 'US';
	return format === 'lower' ? countryCode.toLowerCase() : countryCode.toUpperCase();
};

/**
 * Gets the site language as lowercase 2-character code.
 *
 * @return {string} Lowercase 2 character country code.
 */
export const getLanguage = () => i18nVars.siteLangCode || 'en';

/**
 * Gets the user's country as 2-character code.
 *
 * @param {string} [format=upper] Set casing of return value.
 * @return {string} 2 character country code selected by user.
 */
export const getUserCountryCode = ( format = 'upper' ) => {
	const countryCode = readCookie( COOKIE_USER_COUNTRY_CODE ) || 'US';

	if ( format === 'lower' ) {
		return countryCode.toLowerCase();
	}

	return countryCode.toUpperCase();
};

/**
 * Get the 2-character codes for all supported site countries.
 *
 * @return {Array} All supported country codes
 */
export const getSiteCountryCodes = () => {
	if ( i18nVars.supportedRegions ) {
		const siteCountryCodes = Object.keys( i18nVars.supportedRegions );

		if ( Array.isArray( siteCountryCodes ) ) {
			return siteCountryCodes;
		}
	}

	return [ 'us' ];
};

/**
 * Gets the user's country as a full name.
 *
 * @return {string} Full country name.
 */
export const getUserCountryName = () => readCookie( COOKIE_USER_COUNTRY_NAME ) || 'United States';

/**
 * Gets the relevant currency code for the user's country.
 *
 * @return {string} Country currency code.
 */
export const getUserCurrencyCode = () => {
	const countryCodeCookie = getUserCountryCode();

	return countryCodeCookie in countryToCurrency ? countryToCurrency[ countryCodeCookie ] : 'USD';
};

/**
 * Gets the user region as a lang-country locale code.
 *
 * @return {string} Language-Country code (ex. en-CA).
 */
export const getUserRegionCode = () => `${ getLanguage() }-${ getUserCountryCode( 'lower' ) }`;

/**
 * Sets a cookie with the country code based on user choice (converted to uppercase) for 1 year.
 *
 * @param {string} [countryCode=US] Two letter country code
 */
export const setUserCountryCode = ( countryCode ) => {
	createCookie( COOKIE_USER_COUNTRY_CODE, ( countryCode || 'US' ).toUpperCase(), 365 );
};

/**
 * Sets a cookie with the country name based on user choice (converted to title case) for 1 year.
 *
 * @param {string} [countryName=United States] Full name of country
 */
export const setUserCountryName = ( countryName ) => {
	createCookie(
		COOKIE_USER_COUNTRY_NAME,
		toTitleCase( countryName || 'United States', { exceptions: /^(and|of|the)$/ } ),
		365
	);
};

/**
 * Sets a cookie with the country code based on user location (converted to uppercase) for 1 year.
 *
 * @param {string} [countryCode=US] Two letter country code
 */
export const setUserLocationCountryCode = ( countryCode ) => {
	createCookie( COOKIE_USER_LOCATION_COUNTRY_CODE, ( countryCode || 'US' ).toUpperCase(), 365 );
};

/**
 * Sets a cookie with the user currency code based on user country.
 *
 * @param {string} [currencyCode=US] Two letter country code
 */
export const setUserCurrencyCodeCookie = ( currencyCode ) => {
	createCookie(
		COOKIE_USER_CURRENCY_CODE,
		( currencyCode ? currencyCode : getUserCountryCode() || 'USD' ).toUpperCase(),
		365
	);
};

/**
 * Get an array of all FreshBooks supported countries, with both code and name returned.
 *
 * @return {Array} All supported country codes and names.
 */
export const getAppCountryList = () =>
	countryList
		.map( ( el ) => {
			if ( el.name && el.name.common && el.name.short ) {
				return {
					code: el.name.short,
					name: el.name.common,
				};
			}

			return false;
		} )
		.filter( ( el ) => el );

/**
 * Get an array of all FreshBooks supported country codes.
 *
 * @return {Array} All supported country codes.
 */
export const getAppCountryCodes = () => getAppCountryList().map( ( el ) => el.code );

/**
 * Get an array of all FreshBooks supported country names.
 *
 * @return {Array} All supported country names.
 */
export const getAppCountryNames = () => getAppCountryList().map( ( el ) => el.name );

/**
 * By default, get Fastly country code when present. When user sets their region, this user country code
 * cookie will be updated with that value.
 */
export const setRegionCookies = () => {
	const fastlyValueCode = readCookie( 'country-code' );
	const fastlyValueName = readCookie( 'country-name' );
	const currentUserCountryCode = readCookie( COOKIE_USER_COUNTRY_CODE );
	const currentUserCountryName = readCookie( COOKIE_USER_COUNTRY_NAME );
	const currentUserLocationCountryCode = readCookie( COOKIE_USER_LOCATION_COUNTRY_CODE );

	if ( ! currentUserCountryName ) {
		setUserCountryName( fastlyValueName );
	}

	if ( ! currentUserCountryCode ) {
		setUserCountryCode( fastlyValueCode );
	}

	if ( ! currentUserLocationCountryCode ) {
		setUserLocationCountryCode( fastlyValueCode );
	} else if ( fastlyValueCode && currentUserLocationCountryCode !== fastlyValueCode ) {
		setUserCountryCode( fastlyValueCode );
		setUserLocationCountryCode( fastlyValueCode );
	}

	createCookie( 'billing_country_code', getSiteCountryCode() );
	createCookie( 'fb_language', getLanguage() );
};
